import React, { useCallback, useMemo, useState } from "react";

import { isAxiosError } from "axios";
import { format, isAfter, milliseconds } from "date-fns";
import { useHistory, useLocation } from "react-router-dom";

import useDeleteTradeProposalFromBundle from "@fartherfinance/frontend/api/Investment/hooks/useDeleteTradeProposalFromBundle";
import useGetTradeProposalBundle from "@fartherfinance/frontend/api/Investment/hooks/useGetTradeProposalBundle";
import useStagedOrders from "@fartherfinance/frontend/api/Investment/hooks/useStagedOrders";
import useSubmitTradeProposalBundle from "@fartherfinance/frontend/api/Investment/hooks/useSubmitTradeProposalBundle";
import {
  TradeProposalBundleId,
  TradeProposalId,
} from "@fartherfinance/frontend/api/Investment/types";
import usePutRaiseCash from "@fartherfinance/frontend/api/Liquidations/hooks/usePutRaiseCash";

import { BundleLocationState } from "../types";
import Button from "@src/multiCustodian/components/MUI/Button/Button";
import useAdvisorRequestAuth from "@src/multiCustodian/hooks/useAdvisorRequestAuth";
import useStatusNotification from "@src/multiCustodian/hooks/useStatusNotification";
import Card from "@src/sharedComponents/Card/Card";
import LogoLoadingStill from "@src/sharedComponents/LogoLoadingStill/LogoLoadingStill";
import Skeleton from "@src/sharedComponents/Skeleton/Skeleton";
import Text from "@src/sharedComponents/Typography/Text";
import { pluralize } from "@src/utils/pluralize";

import ProposalTable from "./ProposalBundleDetails/ProposalTable";
import SecuritiesTable from "./ProposalBundleDetails/SecuritiesTable";

import styles from "./ProposalBundleDetails/ProposalBundleDetails.module.css";

interface Props {
  tradeProposalBundleId: TradeProposalBundleId;
}

const ProposalBundleDetails: React.FC<Props> = (props) => {
  const [isMutating, setIsMutating] = useState(false);

  const history = useHistory();
  const { state } = useLocation<BundleLocationState>();

  const notification = useStatusNotification();

  const auth = useAdvisorRequestAuth();

  const putRaiseCash = usePutRaiseCash(auth);

  const bundle = useGetTradeProposalBundle(
    props.tradeProposalBundleId,
    auth,
    false,
    { refetchInterval: milliseconds({ seconds: 10 }) }
  );

  // Fetch the staged orders to check if the staging area is dirty
  const stagedOrders = useStagedOrders(auth, false, {
    refetchInterval: milliseconds({ seconds: 10 }),
  });

  const removeProposalWithApi = useDeleteTradeProposalFromBundle(
    props.tradeProposalBundleId,
    auth
  );

  const removeProposal = useCallback(
    async (proposalId: TradeProposalId) => {
      if (isMutating) {
        notification(
          "Please wait for the current operation to finish",
          "Error"
        );
        return;
      }

      try {
        setIsMutating(true);
        await removeProposalWithApi(proposalId);
      } catch (_err) {
        notification("Failed to remove proposal", "Error");
      } finally {
        setIsMutating(false);
      }
    },
    [isMutating, notification, removeProposalWithApi]
  );

  const submitBundleApi = useSubmitTradeProposalBundle(auth);

  const submitBundle = useCallback(
    async (tradeProposalBundleId: TradeProposalBundleId) => {
      if (bundle.data === undefined) {
        return;
      }

      try {
        setIsMutating(true);

        await submitBundleApi(tradeProposalBundleId);

        notification("Bundle submitted successfully", "Success");

        // need to update raise cash requests (set them to next status/state)
        const liquidationProposals = bundle.data.tradeProposals.filter(
          (tp) => tp.rebalancer === "LiquidationRebalancer"
        );

        if (liquidationProposals.length >= 1) {
          const promises = liquidationProposals.map((lp) =>
            putRaiseCash({
              planAssignmentId: lp.portfolioPlanAssignmentId,
              body: {
                status: "PendingLiquidationTaskCompletion",
              },
            })
          );

          const results = await Promise.allSettled(promises);
          const successes = results.filter((res) => res.status === "fulfilled");

          if (successes.length >= 1) {
            notification(
              `${pluralize(
                successes.length,
                "Raise Cash request",
                true
              )} updated`,
              "Success"
            );
          }

          if (results.length - successes.length >= 1) {
            notification(
              `${pluralize(
                results.length - successes.length,
                "Raise Cash request",
                true
              )} failed to update`,
              "Error"
            );
          }
        }
      } catch (err) {
        if (isAxiosError(err) && err.response?.status === 400) {
          const errorMessage = (
            err.response.data as unknown as { message: string }
          ).message;
          notification(errorMessage, "Error");
        } else {
          console.error(err);
          notification("Bundle submission failed", "Error");
        }
      } finally {
        setIsMutating(false);
      }
    },
    [notification, submitBundleApi, bundle.data, putRaiseCash]
  );

  const isExpired: boolean | null = useMemo(() => {
    if (bundle.data === undefined) {
      return null;
    }

    return isAfter(new Date(), bundle.data.expiresAt);
  }, [bundle.data]);

  const canSubmit: boolean | null = useMemo(() => {
    if (bundle.data === undefined) {
      return null;
    }

    return bundle.data.status === "Draft";
  }, [bundle.data]);

  // Check if the staging area is dirty (has existing orders)
  const isStagingDirty = useMemo(() => {
    if (stagedOrders.isLoading || stagedOrders.hasError) {
      return false; // Default to allow submission if we can't determine
    }

    return stagedOrders.data.orders.length > 0;
  }, [stagedOrders]);

  const showErrorBar: boolean = (isExpired ?? false) || isStagingDirty;

  const submitButtonIsDisabled =
    bundle.data === undefined || // Still loading data
    isMutating || // we are mutating
    isExpired === true || // This is an expired bundle
    canSubmit === false || // This bundle cannot be submitted due to state
    isStagingDirty; // Staging area has orders that need to be processed first

  const onReturnToBundleTable = useCallback(() => {
    history.push("/Advisor/FinancialOperations/TradeProposalBundles", state);
  }, [history, state]);

  return (
    <>
      {isMutating && <LogoLoadingStill onTop={true} />}

      <div id="proposal-details-header" className={styles.Header}>
        <Button
          text="Return to Bundle Table"
          variant="outlined"
          buttonType="secondary"
          onClick={onReturnToBundleTable}
        />

        <div className={styles.SideBySide}>
          <div className={styles.FlexRow}>
            <span>Created</span>:&nbsp;
            {bundle.data === undefined ? (
              <Skeleton width={150} />
            ) : (
              <span>{format(bundle.data.createdAt, "M/d/yyyy - hh:mm a")}</span>
            )}
          </div>

          <div className={styles.FlexRow}>
            <span>Expires</span>:&nbsp;
            {bundle.data === undefined ? (
              <Skeleton width={150} />
            ) : (
              <span>{format(bundle.data.expiresAt, "M/d/yyyy - hh:mm a")}</span>
            )}
          </div>
        </div>

        <div className={styles.SideBySide}>
          <Button
            text="Submit Bundle"
            variant="contained"
            buttonType="info"
            disabled={submitButtonIsDisabled}
            onClick={() => submitBundle(props.tradeProposalBundleId)}
          />
        </div>
      </div>

      <div className={styles.VerticalSeparator} />

      <Card header={props.tradeProposalBundleId}>
        <Text>Status: {bundle.data?.status ?? <Skeleton width={100} />}</Text>
        <Text>
          Version:{" "}
          {bundle.data?.version?.toString() ?? <Skeleton width={100} />}
        </Text>
        <Text>Type: {bundle.data?.type ?? <Skeleton width={100} />}</Text>
        <Text>
          Created:{" "}
          {bundle.data ? (
            format(bundle.data.createdAt, "M/d/yyyy - hh:mm a")
          ) : (
            <Skeleton width={100} />
          )}
        </Text>
        <Text>
          Updated:{" "}
          {bundle.data ? (
            format(bundle.data.updatedAt, "M/d/yyyy - hh:mm a")
          ) : (
            <Skeleton width={100} />
          )}
        </Text>
      </Card>

      <div className={styles.VerticalSeparator} />

      {showErrorBar === true ? (
        <>
          <div className={styles.TopRow}>
            {isExpired === true ? (
              <Card header="Expired">
                <Text>This bundle is expired and cannot be submitted</Text>
              </Card>
            ) : null}

            {isStagingDirty === true ? (
              <Card header="Staging Area Not Empty">
                <Text>
                  The staging area has pending orders. Please process or clear
                  these orders before submitting this bundle.
                </Text>
              </Card>
            ) : null}
          </div>

          <div className={styles.VerticalSeparator} />
        </>
      ) : null}

      <Text variant="title1">Proposals in Bundle</Text>

      <ProposalTable
        tradeProposalBundleId={props.tradeProposalBundleId}
        removeProposal={removeProposal}
        isMutating={isMutating}
        isInteractionDisabled={submitButtonIsDisabled}
      />

      <div className={styles.VerticalSeparator} />

      <Text variant="title1">Securities in all proposals</Text>

      <SecuritiesTable tradeProposalBundleId={props.tradeProposalBundleId} />

      <div className={styles.VerticalSeparator} />
      <div className={styles.VerticalSeparator} />

      <div className={styles.BottomRow}>
        <div>
          {isExpired === true ? (
            <div>This bundle is expired and cannot be submitted</div>
          ) : null}
          {isStagingDirty === true ? (
            <div>
              The staging area has pending orders that need to be processed
              first
            </div>
          ) : null}
        </div>
        <div>
          <Button
            text="Submit Bundle"
            variant="contained"
            buttonType="info"
            disabled={submitButtonIsDisabled}
            onClick={() => submitBundle(props.tradeProposalBundleId)}
          />
        </div>
      </div>
    </>
  );
};

export default ProposalBundleDetails;
